import { useRouteMatch } from 'react-router-dom';
import { ProjectRouteParams } from '../project/projectRouteParams';
import { RouteTemplates } from './routes';
import * as React from 'react';
import { useMemo } from 'react';
import { useAppState } from '../core/redux/hooks';
import { RaterApprovalRouteParams } from '../raterApproval/raterApprovalRouteParams';
import { useCollaborationProjIds } from '../project/devPlan/collaboration/hooks';
import { useCurrentUser } from '../auth/hooks';


export type ProjIds = {
  projId: number;
  projDisplayId: string;
  participantId: number;
}

/**
 * A hook that returns the basic project IDs we care about:
 * Display ID
 * ID
 * Participant ID
 */
export const useProjIds = (): ProjIds => {
  const match = useRouteMatch<ProjectRouteParams>(RouteTemplates.Project);

  const routePartId     = React.useMemo(() => +(match?.params.participantId ?? ''), [match]);
  const routeProjDispId = React.useMemo(() => match?.params.projectId ?? '', [match]);

  const statePartId = useAppState(state => state.auth.currentUser?.personId);
  const projId      = useAppState(state => state.displayIdMapping[routeProjDispId]);

  return {
    projId:        projId || -1,
    projDisplayId: routeProjDispId || '',
    participantId: statePartId || routePartId || -1,
  };
};


export type RaterApprovalIds = {
  participantId: number;
  projDisplayId: string;
}

/**
 * A hook that returns the basic rater approval IDs:
 * Participant ID
 * Project Display ID
 */
export const useRaterApprovalIds = (): RaterApprovalIds => {
  const match = useRouteMatch<RaterApprovalRouteParams>(RouteTemplates.RaterApprovalDashboard);

  const routePartId     = React.useMemo(() => +(match?.params.participantId ?? ''), [match]);
  const routeProjDispId = React.useMemo(() => match?.params.projectId ?? '', [match]);

  const stateProjDispId = useAppState(state => state.projectSummary?.displayProjectId);

  return {
    participantId: routePartId || -1,
    projDisplayId: routeProjDispId || stateProjDispId || '',
  };
};


export const useProjIdsForUserType = () => {
  const isParticipant = !!useCurrentUser();
  const participantProjIds  = useProjIds();
  const collaboratorProjIds = useCollaborationProjIds();

  return useMemo(
    () => isParticipant ? participantProjIds : collaboratorProjIds,
    [collaboratorProjIds, isParticipant, participantProjIds],
  );
};
